// Scanner helpers for class-variable `var(...)` references and identifiers.
// Example: parseVarReferenceAt("var(--theme.accent)", 0).
import type { ParsedVarReference } from './types'
import { isDigit, isIdentifierChar, isIdentifierStart, skipWhitespace } from './scanner-chars'

// Parse var reference token at exact source index.
export function parseVarReferenceAt(source: string, startIndex: number): ParsedVarReference | null {
  // Reject index when token does not start with `var(`.
  if (!source.startsWith('var(', startIndex)) {
    // Return null for non-var token positions.
    return null
  } else {
    // Continue parsing var reference token.
  }

  let cursor = startIndex + 4
  cursor = skipWhitespace(source, cursor)
  // Reject invalid references without custom-property prefix.
  if (source[cursor] !== '-' || source[cursor + 1] !== '-') {
    return null
  } else {
    // Continue scanning variable name.
  }

  cursor += 2
  // Reject names with invalid start char.
  if (!isIdentifierStart(source[cursor] ?? '')) {
    return null
  } else {
    // Continue scanning identifier body.
  }

  const nameStart = cursor
  cursor += 1
  // Consume custom-property name characters.
  while (cursor < source.length && isIdentifierChar(source[cursor])) {
    cursor += 1
  }

  const variableName = `--${source.slice(nameStart, cursor)}`
  let arrayIndex: number | null = null
  // Consume optional array index access segment after variable name.
  if (source[cursor] === '[') {
    cursor += 1
    cursor = skipWhitespace(source, cursor)
    // Reject malformed array index access without numeric index.
    if (!isDigit(source[cursor] ?? '')) {
      return null
    } else {
      // Continue array index digit scanning.
    }

    const indexStart = cursor
    cursor += 1
    // Consume array index digits.
    while (cursor < source.length && isDigit(source[cursor])) {
      cursor += 1
    }

    cursor = skipWhitespace(source, cursor)
    // Reject malformed array index access without closing bracket.
    if (source[cursor] !== ']') {
      return null
    } else {
      // Continue parsed array index finalization.
    }

    arrayIndex = Number.parseInt(source.slice(indexStart, cursor), 10)
    cursor += 1
  } else {
    // Keep array index empty when bracket suffix is absent.
  }

  let objectKey: string | null = null
  // Consume optional object key path after dot.
  if (source[cursor] === '.') {
    cursor += 1
    // Reject malformed dotted references without key start.
    if (!isObjectKeyStart(source[cursor] ?? '')) {
      return null
    } else {
      // Continue dotted key scanning.
    }

    const keyStart = cursor
    cursor += 1
    // Consume key segment characters.
    while (cursor < source.length && isIdentifierChar(source[cursor])) {
      cursor += 1
    }

    objectKey = source.slice(keyStart, cursor)
  } else {
    // Keep object key empty when dotted suffix is absent.
  }

  cursor = skipWhitespace(source, cursor)
  // Reject malformed reference without closing parenthesis.
  if (source[cursor] !== ')') {
    return null
  } else {
    // Return parsed var reference descriptor.
    const end = cursor + 1
    return {
      start: startIndex,
      end,
      raw: source.slice(startIndex, end),
      variableName,
      objectKey,
      arrayIndex,
    }
  }
}

// Detect valid object-key segment starts, including numeric-leading keys.
function isObjectKeyStart(char: string): boolean {
  // Return true for alpha-start and numeric-leading object key segments.
  return isIdentifierStart(char) || isDigit(char)
}

// Consume exact `&` prefix used by adjacency/composition syntax.
export function consumeAmpersandPrefix(
  source: string,
  startIndex: number,
): { startIndex: number; nextIndex: number } | null {
  // Reject when current character is not ampersand.
  if (source[startIndex] !== '&') {
    return null
  } else {
    // Return exact ampersand prefix range without skipping spaces.
    const nextIndex = startIndex + 1
    return {
      startIndex,
      nextIndex,
    }
  }
}
